import React from 'react';
import { createPortal } from 'react-dom';

const DROPDOWN_GAP = 4;
const VIEWPORT_PADDING = 8;

export function computeDropdownLayout(
  btnRect: DOMRect,
  panelHeight: number,
  panelWidth: number,
): { top: number; left: number; maxHeight?: number } {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let left = btnRect.left;
  const clampedWidth = Math.max(panelWidth, 170);
  if (left + clampedWidth > viewportW - VIEWPORT_PADDING) {
    left = Math.max(VIEWPORT_PADDING, viewportW - clampedWidth - VIEWPORT_PADDING);
  }

  const spaceBelow = viewportH - btnRect.bottom - DROPDOWN_GAP - VIEWPORT_PADDING;
  const spaceAbove = btnRect.top - DROPDOWN_GAP - VIEWPORT_PADDING;
  const preferBelow = spaceBelow >= spaceAbove;

  if (preferBelow) {
    if (panelHeight <= spaceBelow) {
      return { top: btnRect.bottom + DROPDOWN_GAP, left };
    }
    return {
      top: btnRect.bottom + DROPDOWN_GAP,
      left,
      maxHeight: Math.max(120, spaceBelow),
    };
  }

  if (panelHeight <= spaceAbove) {
    return { top: btnRect.top - panelHeight - DROPDOWN_GAP, left };
  }

  const maxHeight = Math.max(120, spaceAbove);
  return {
    top: btnRect.top - maxHeight - DROPDOWN_GAP,
    left,
    maxHeight,
  };
}

export type LightDropdownProps = {
  label: React.ReactNode;
  buttonClass: string;
  buttonStyle?: React.CSSProperties;
  menuClassName?: string;
  children: (close: () => void) => React.ReactNode;
  /** Stretch trigger to 100% of parent width (major pickers). */
  fullWidth?: boolean;
  disabled?: boolean;
  /** Prefer panel width ≥ trigger width (long major lists). */
  matchTriggerWidth?: boolean;
  'aria-label'?: string;
};

/**
 * Viewport-aware dropdown for My Colleges cards.
 * Portals to document.body and flips above the trigger when bottom space is tight.
 */
export function LightDropdown({
  label,
  buttonClass,
  buttonStyle,
  menuClassName,
  children,
  fullWidth = false,
  disabled = false,
  matchTriggerWidth = false,
  'aria-label': ariaLabel,
}: LightDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [layout, setLayout] = React.useState<{
    top: number;
    left: number;
    maxHeight?: number;
  } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const btnRectRef = React.useRef<DOMRect | null>(null);

  const close = React.useCallback(() => {
    setIsOpen(false);
    setLayout(null);
    btnRectRef.current = null;
  }, []);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (isOpen) {
      close();
      return;
    }
    btnRectRef.current = btnRef.current?.getBoundingClientRect() ?? null;
    setIsOpen(true);
    setLayout(null);
  };

  const reposition = React.useCallback(() => {
    if (!btnRef.current || !panelRef.current) return;
    const btnRect = btnRef.current.getBoundingClientRect();
    const panel = panelRef.current;
    const minWidth = matchTriggerWidth
      ? Math.max(btnRect.width, 170)
      : Math.max(panel.offsetWidth, 170);
    const next = computeDropdownLayout(
      btnRect,
      panel.scrollHeight,
      minWidth,
    );
    setLayout((prev) => {
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.maxHeight === next.maxHeight
      ) {
        return prev;
      }
      return next;
    });
  }, [matchTriggerWidth]);

  React.useLayoutEffect(() => {
    if (!isOpen || !btnRectRef.current || !panelRef.current) return;
    reposition();
  }, [isOpen, reposition]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onScroll = (e: Event) => {
      const target = e.target;
      if (
        panelRef.current &&
        target instanceof Node &&
        panelRef.current.contains(target)
      ) {
        return;
      }
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [isOpen, close]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onResize = () => reposition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, reposition]);

  const measureRect = btnRectRef.current;
  const triggerWidth = measureRect?.width;

  return (
    <div style={{ flexShrink: fullWidth ? undefined : 0, width: fullWidth ? '100%' : undefined }}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={buttonClass}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          width: fullWidth ? '100%' : undefined,
          ...buttonStyle,
        }}
      >
        {typeof label === 'string' ? <span>{label}</span> : label}
      </button>

      {isOpen &&
        createPortal(
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={close} />
            <div
              ref={panelRef}
              className="gm-light-dropdown-panel"
              style={{
                position: 'fixed',
                zIndex: 9999,
                top: layout?.top ?? (measureRect ? measureRect.bottom + DROPDOWN_GAP : 0),
                left: layout?.left ?? measureRect?.left ?? 0,
                visibility: layout ? 'visible' : 'hidden',
                maxHeight: layout?.maxHeight,
                overflow: layout?.maxHeight ? 'hidden' : 'visible',
                minWidth: matchTriggerWidth && triggerWidth ? triggerWidth : undefined,
                width: matchTriggerWidth && triggerWidth ? triggerWidth : undefined,
              }}
            >
              <div
                className={menuClassName ? `gm-light-dropdown-menu ${menuClassName}` : 'gm-light-dropdown-menu'}
                onWheel={(e) => e.stopPropagation()}
              >
                {children(close)}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
